import { Pagination } from "@core/domain/models/pagination";
import { BaseViewModel } from "@core/presentacion/view-model/base/base.viewmodel";
import { inject, injectable } from "inversify";
import { action, computed, makeObservable, observable, runInAction } from "mobx";
import { GetAllBudgetTemplatesUseCase } from "@project/domain/usecases/budget-template/get-all-budget-templates.usecase";
import { CreateBudgetFromTemplateUseCase } from "@project/domain/usecases/budget-template/create-budget-from-template.usecase";
import { CreateTemplateFromTemplateUseCase } from "@project/domain/usecases/budget-template/create-template-from-template.usecase";
import { MarkAsTemplateUseCase } from "@project/domain/usecases/budget-template/mark-as-template.usecase";
import { DeleteBudgetUseCase } from "@project/domain/usecases/budget/delete-budget.usecase";
import { BudgetTemplate } from "@project/domain/models/budget-template/budget-template";

export interface BudgetTemplateTable {
    id: number;
    name: string;
    description: string;
    created: string;
    modified: string;
}

interface BudgetTemplatesTable {
    budgetTemplates: BudgetTemplateTable[];
    count: number;
}

@injectable()
export class BudgetTemplateListViewModel extends BaseViewModel {
    @observable initialLoading: boolean = true;
    @observable budgetTemplates: BudgetTemplateTable[] = [];
    @observable templates: BudgetTemplate[] = [];
    @observable pagination: Pagination = new Pagination();
    @observable isLoading: boolean = false;
    @observable totalTemplates: number = 0;

    constructor(
        @inject(GetAllBudgetTemplatesUseCase)
        private readonly getAllBudgetTemplatesUseCase: GetAllBudgetTemplatesUseCase,
        @inject(CreateBudgetFromTemplateUseCase)
        private readonly createBudgetFromTemplateUseCase: CreateBudgetFromTemplateUseCase,
        @inject(CreateTemplateFromTemplateUseCase)
        private readonly createTemplateFromTemplateUseCase: CreateTemplateFromTemplateUseCase,
        @inject(MarkAsTemplateUseCase)
        private readonly markAsTemplateUseCase: MarkAsTemplateUseCase,
        @inject(DeleteBudgetUseCase)
        private readonly deleteBudgetUseCase: DeleteBudgetUseCase,
    ) {
        super();
        makeObservable(this);
    }

    @computed
    get budgetTemplatesTable(): BudgetTemplatesTable {
        return {
            count: this.budgetTemplates.length,
            budgetTemplates: this.budgetTemplates,
        };
    }

    @computed
    get currentPage(): number {
        return this.pagination.page;
    }

    @computed
    get pageSize(): number {
        return this.pagination.pageSize;
    }

    @computed
    get totalItems(): number {
        return this.totalTemplates;
    }

    override async didMount(): Promise<void> {
        await this.fetchTemplates();
        runInAction(() => {
            this.initialLoading = false;
        });
    }

    @action
    setPagination = (page: number, pageSize: number) => {
        this.pagination.page = page;
        this.pagination.pageSize = pageSize;
        this.fetchTemplates();
    };

    @action
    handlePageChange = (page: number, pageSize: number) => {
        this.setPagination(page, pageSize);
    };

    async fetchTemplates(): Promise<void> {
        this.isLoading = true;
        try {
            const templates = await this.getAllBudgetTemplatesUseCase.execute();
            runInAction(() => {
                this.templates = templates;
                this.budgetTemplates = templates.map((t: BudgetTemplate) => ({
                    id: t.id,
                    name: t.name,
                    description: t.description ?? "",
                    created: t.created,
                    modified: t.modified,
                }));
                this.totalTemplates = templates.length;
            });
        } catch (error) {
            console.error("Error fetching budget templates:", error);
        } finally {
            runInAction(() => {
                this.isLoading = false;
            });
        }
    }

    async deleteTemplate(templateId: number): Promise<boolean> {
        const success = await this.deleteBudgetUseCase.execute(templateId);
        if (success) {
            // Refresh the list after successful deletion
            await this.fetchTemplates();
        }
        return success;
    }

    async createTemplateFromTemplate(templateId: number, name: string, description: string): Promise<boolean> {
        const result = await this.createTemplateFromTemplateUseCase.execute(templateId, name, description);
        if (result.isLeft()) return false;
        await this.fetchTemplates();
        return true;
    }
}
